/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useMemo } from 'react';

/* Import components */
import AppLoader from '@/components/feedback/AppLoader';
import SensorRequestMessage from '@/components/feedback/SensorRequestMessage';

import { useI18n } from '@/context/I18nContext';

/* Import libraries */
import { UnitConverter } from '@/lib/unit/UnitConverter';

/* Import types */
import type {
    BuildingStatisticsData,
    BuildingStatisticsSupply,
    BuildingStatisticsSupplyData,
} from '@/types/eeCheck';

type BuildingStatisticsSupplyKey = 'pv' | 'grid_feed' | 'grid_supply' | 'heat' | 'water' | 'gas';
export type BuildingStatisticsSeriesType = 'daily_W' | 'daily_W_Abs' | 'monthly_W' | 'yearly_W';

interface SystemStatisticsTableProps {
    seriesType?: BuildingStatisticsSeriesType;
    statistics: BuildingStatisticsData | null;
    targetUnits?: Partial<Record<BuildingStatisticsSupplyKey, string>>;
    errorMessage?: string;
    isLoading?: boolean;
    fractionDigits?: Partial<Record<BuildingStatisticsSupplyKey, number>>;
}

interface SupplyColumn {
    key: BuildingStatisticsSupplyKey;
    label: string;
}

interface SeriesValue {
    timestamp: number;
    value: number;
}

const SUPPLY_COLUMNS: SupplyColumn[] = [
    { key: 'pv', label: 'Photovoltaics' },
    { key: 'grid_feed', label: 'Grid feed' },
    { key: 'grid_supply', label: 'Electricity' },
    { key: 'heat', label: 'Heat' },
    { key: 'water', label: 'Water' },
    { key: 'gas', label: 'Gas' },
];

function isAvailableSupply(supply: BuildingStatisticsSupplyData): supply is BuildingStatisticsSupply {
    return !Array.isArray(supply);
}

function getSeriesValues(
    supply: BuildingStatisticsSupplyData,
    seriesType: BuildingStatisticsSeriesType,
): SeriesValue[] {
    if (!isAvailableSupply(supply)) {
        return [];
    }

    const { timestamps, values } = supply[seriesType];
    const valueCount = Math.min(timestamps.length, values.length);

    return Array.from({ length: valueCount }, (_, index) => ({
        timestamp: timestamps[index],
        value: values[index],
    })).filter(({ timestamp, value }) => Number.isFinite(timestamp) && Number.isFinite(value));
}

function getDisplayUnit(
    sourceUnit: string,
    targetUnit: string | undefined,
): { factor: number; unit: string } {
    if (!targetUnit) {
        return { factor: 1, unit: sourceUnit };
    }

    const factor = new UnitConverter(sourceUnit, targetUnit).getFactor();

    return factor === null
        ? { factor: 1, unit: sourceUnit }
        : { factor, unit: targetUnit };
}

function formatTimeColumnValue(
    timestamp: number,
    locale: string,
    seriesType: BuildingStatisticsSeriesType,
): string {
    if (seriesType === 'yearly_W') {
        return new Intl.DateTimeFormat(locale, {
            timeZone: 'Europe/Berlin',
            year: 'numeric',
        }).format(timestamp * 1000);
    }

    const parts = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        timeZone: 'Europe/Berlin',
        year: 'numeric',
    }).formatToParts(timestamp * 1000);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((currentPart) => currentPart.type === type)?.value ?? '';
    const monthYear = `${part('month')}.${part('year')}`;

    return seriesType === 'monthly_W' ? monthYear : `${part('day')}.${monthYear}`;
}

function getTimeColumnLabel(seriesType: BuildingStatisticsSeriesType): string {
    if (seriesType === 'yearly_W') {
        return 'Year';
    }

    return seriesType === 'monthly_W' ? 'Month' : 'Day';
}

function formatTimestampTooltip(timestamp: number, locale: string): string {
    return new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'medium',
        timeZone: 'Europe/Berlin',
    }).format(timestamp * 1000);
}

function formatValue(value: number, locale: string, fractionDigits: number | undefined): string {
    if (fractionDigits === undefined || !Number.isInteger(fractionDigits) || fractionDigits < 0) {
        return String(value);
    }

    return new Intl.NumberFormat(locale, {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
    }).format(value);
}

function SystemStatisticsTable({
    seriesType = 'yearly_W',
    statistics,
    targetUnits,
    errorMessage = '',
    isLoading = false,
    fractionDigits,
}: SystemStatisticsTableProps) {
    const { locale, t } = useI18n();

    const seriesValuesBySupply = useMemo(() => {
        if (!statistics) {
            return new Map<BuildingStatisticsSupplyKey, SeriesValue[]>();
        }

        return new Map(SUPPLY_COLUMNS.map(({ key }) => [key, getSeriesValues(statistics[key], seriesType)]));
    }, [seriesType, statistics]);
    const timestamps = useMemo(() => Array.from(new Set(
        Array.from(seriesValuesBySupply.values()).flatMap((values) => values.map(({ timestamp }) => timestamp)),
    )).sort((left, right) => right - left), [seriesValuesBySupply]);
    const valuesBySupplyAndTimestamp = useMemo(() => new Map(
        SUPPLY_COLUMNS.map(({ key }) => [
            key,
            new Map((seriesValuesBySupply.get(key) ?? []).map(({ timestamp, value }) => [timestamp, value])),
        ]),
    ), [seriesValuesBySupply]);

    if (isLoading) {
        return <AppLoader label={t('Loading API request...')} />;
    }

    if (errorMessage) {
        return (
            <div className="mt-4 rounded-box border border-error/30 bg-error/10 px-3 py-2 text-sm leading-relaxed text-error">
                <SensorRequestMessage message={errorMessage} />
            </div>
        );
    }

    if (!statistics) {
        return null;
    }

    const tableTimestamps = timestamps.length > 0 ? timestamps : [null];

    return (
        <div className="w-full overflow-x-auto p-6">
            <table className="table table-zebra">
                <thead>
                    <tr>
                        <th className="align-top">{t(getTimeColumnLabel(seriesType))}</th>
                        {SUPPLY_COLUMNS.map(({ key, label }) => {
                            const supply = statistics[key];
                            const hasValues = (seriesValuesBySupply.get(key) ?? []).length > 0;
                            const availableSupply = isAvailableSupply(supply) ? supply : null;
                            const displayUnit = availableSupply
                                ? getDisplayUnit(availableSupply[seriesType].unit, targetUnits?.[key])
                                : null;
                            const sensorName = availableSupply?.urn;
                            const sourceUrn = availableSupply?.[seriesType].urn;
                            const sensorTooltip = sensorName && sourceUrn
                                ? `${sensorName}: ${sourceUrn}`
                                : sensorName;
                            const showSensorTooltip = hasValues && Boolean(sensorTooltip);

                            return (
                                <th key={key} className="align-top">
                                    <div
                                        className={`flex flex-col items-start${showSensorTooltip ? ' cursor-help' : ''}`}
                                        title={showSensorTooltip ? sensorTooltip : undefined}
                                    >
                                        <span className="font-semibold">{t(label)}</span>
                                        {hasValues && displayUnit?.unit ? (
                                            <span className="mt-1 flex w-full flex-col gap-0.5 border-t border-base-content/10 pt-1 text-xs font-normal text-base-content/65">
                                                <span>{displayUnit.unit}</span>
                                            </span>
                                        ) : null}
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {tableTimestamps.map((timestamp) => (
                        <tr key={timestamp ?? 'no-values'}>
                            <td>
                                {timestamp === null
                                    ? '-'
                                    : (
                                        <span
                                            className="tooltip tooltip-top"
                                            data-tip={formatTimestampTooltip(timestamp, locale)}
                                        >
                                            {formatTimeColumnValue(timestamp, locale, seriesType)}
                                        </span>
                                    )}
                            </td>
                            {SUPPLY_COLUMNS.map(({ key }) => {
                                const sourceValue = timestamp === null
                                    ? undefined
                                    : valuesBySupplyAndTimestamp.get(key)?.get(timestamp);
                                const supply = statistics[key];
                                const factor = isAvailableSupply(supply)
                                    ? getDisplayUnit(supply[seriesType].unit, targetUnits?.[key]).factor
                                    : 1;
                                const value = sourceValue === undefined ? undefined : sourceValue * factor;

                                return (
                                    <td key={key} className="font-medium tabular-nums">
                                        {value === undefined
                                            ? '-'
                                            : formatValue(value, locale, fractionDigits?.[key])}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default SystemStatisticsTable;
